import {
  ApplicantAngelInvestment,
  ApplicantArticle,
  ApplicantAward,
  ApplicantCommunity,
  ApplicantCompany,
  ApplicantContest,
  ApplicantEntrepreneurship,
  ApplicantMentionInMedia,
  ApplicantMentorship,
  ApplicantOpenSource,
  ApplicantPatent,
  ApplicantPublicTalk,
  ApplicantReferee,
  ApplicantScientificWork,
  User,
} from "@prisma/client";
import { ApplicationForm } from "@prisma/client";
import { TFunction } from "next-i18next";
import { useCallback, useEffect, useState } from "react";

import { ProfJourneyFormNamespace } from "@/containers/ProfJourney";

import {
  ApplicationSectionColumn,
  ApplicationSectionStatusType,
  getApplicationSectionStatus,
  getApplicationSectionStatusFromArrayItem,
} from "./applicationSectionStatus";
import { isAdmin } from "./auth/rbac";
import {
  ApplicantCompanyTmpField,
  ApplicantEntrepreneurshipTmpField,
} from "./experienceType";
import { ApplicationFull } from "./prisma";

export enum ApplicantPlan {
  FullSupport = 1,
  LimitedSupport = 2,
  SelfService = 3,
}

export const getApplicantPlanName = (plan: ApplicantPlan) => {
  if (plan === ApplicantPlan.FullSupport) {
    return "Full Support";
  }
  if (plan === ApplicantPlan.LimitedSupport) {
    return "Limited Support";
  }
  if (plan === ApplicantPlan.SelfService) {
    return "Self Service";
  }
};

export const hasPermissionForApplicationView = (
  user: User,
  application: ApplicationFull
) => {
  return isAdmin(user) || user.id === application?.applicant?.user?.id;
};

export const getExperienceYears = (
  dateFrom: Date | null,
  dateTo: Date | null,
  t: TFunction
) => {
  const isSameYears = dateFrom?.getFullYear() === dateTo?.getFullYear();
  return dateFrom && isSameYears
    ? `${dateFrom?.getFullYear()}`
    : dateFrom
    ? ` ${dateFrom.getFullYear()} – ${
        dateTo
          ? dateTo.getFullYear()
          : t("application:getExperienceYears.text.now")
      }`
    : ``;
};

export const useIsProfJourneyValid = (
  application: ApplicationFull,
  listForCompany: ApplicantCompanyTmpField[],
  listForEntrepreneurship: ApplicantEntrepreneurshipTmpField[],
  onValidityChange: (isValid: boolean) => void,
  isSubItemsCountValid?: boolean
) => {
  const [isValid, setIsValid] = useState(false);

  const checkIsValid = useCallback(() => {
    let isValidCount =
      listForCompany.length > 0 || listForEntrepreneurship.length > 0;

    // Check minimum 2 projects or milestones inside, if applicant have only one employment or entrepreneurship
    if (listForCompany.length + listForEntrepreneurship.length === 1) {
      isValidCount = !!isSubItemsCountValid;
    }

    // Check also any items have review completed status
    const isValidEmployment = listForCompany.every(
      (x) =>
        getApplicationSectionStatusFromArrayItem(
          application,
          ApplicationSectionColumn.Employment,
          x.id
        ) === ApplicationSectionStatusType.ReviewCompleted
    );
    const isValidEntrepreneurship = listForEntrepreneurship.every(
      (x) =>
        getApplicationSectionStatusFromArrayItem(
          application,
          ApplicationSectionColumn.Entrepreneurship,
          x.id
        ) === ApplicationSectionStatusType.ReviewCompleted
    );
    return isValidCount && isValidEmployment && isValidEntrepreneurship;
  }, [
    application,
    listForCompany,
    listForEntrepreneurship,
    isSubItemsCountValid,
  ]);

  useEffect(() => {
    const isValidNow = checkIsValid();
    if (isValid !== isValidNow) {
      setIsValid(isValidNow);
      onValidityChange(isValidNow);
    }
  }, [
    application,
    listForCompany,
    listForEntrepreneurship,
    isSubItemsCountValid,
    checkIsValid,
    isValid,
    setIsValid,
    onValidityChange,
  ]);
};

export const useIsExternalProfileValid = (values: {
  application: ApplicationFull;
  listForArticles: ApplicantArticle[];
  listForPublicTalks: ApplicantPublicTalk[];
  listForScientificWork: ApplicantScientificWork[];
  listForContests: ApplicantContest[];
  listForAward: ApplicantAward[];
  listForPatent: ApplicantPatent[];
  listForOpenSource: ApplicantOpenSource[];
  listForTeachingAndMentorship: ApplicantMentorship[];
  listForCommunity: ApplicantCommunity[];
  listForAngelInvestment: ApplicantAngelInvestment[];
  listForMentionInMedia: ApplicantMentionInMedia[];
  listForEntrepreneurship: ApplicantEntrepreneurship[];
  listForCompany: ApplicantCompany[];
  onValidityChange: (isValid: boolean) => void;
}) => {
  const {
    application,
    listForArticles,
    listForPublicTalks,
    listForContests,
    listForAward,
    listForPatent,
    listForOpenSource,
    listForTeachingAndMentorship,
    listForCommunity,
    listForAngelInvestment,
    listForScientificWork,
    listForMentionInMedia,
    listForEntrepreneurship,
    listForCompany,
    onValidityChange,
  } = values;
  const [isValid, setIsValid] = useState(false);

  const checkIsValid = useCallback(() => {
    const hasAnyItems =
      listForArticles.length > 0 ||
      listForPublicTalks.length > 0 ||
      listForContests.length > 0 ||
      listForAward.length > 0 ||
      listForPatent.length > 0 ||
      listForOpenSource.length > 0 ||
      listForTeachingAndMentorship.length > 0 ||
      listForCommunity.length > 0 ||
      listForScientificWork.length > 0 ||
      listForMentionInMedia.length > 0 ||
      listForAngelInvestment.length > 0 ||
      listForEntrepreneurship.length > 0 ||
      listForCompany.length > 0;
    const isValid = hasAnyItems
      ? getApplicationSectionStatus(
          application,
          ApplicationSectionColumn.ExternalProfile
        ) === ApplicationSectionStatusType.ReviewCompleted
      : false;
    return isValid;
  }, [
    application,
    listForMentionInMedia,
    listForArticles,
    listForPublicTalks,
    listForContests,
    listForAward,
    listForPatent,
    listForOpenSource,
    listForTeachingAndMentorship,
    listForCommunity,
    listForAngelInvestment,
    listForScientificWork,
    listForEntrepreneurship,
    listForCompany,
  ]);

  useEffect(() => {
    const isValidNow = checkIsValid();
    if (isValid !== isValidNow) {
      setIsValid(isValidNow);
      onValidityChange(isValidNow);
    }
  }, [
    application,
    listForArticles,
    listForPublicTalks,
    listForContests,
    listForAward,
    listForPatent,
    listForOpenSource,
    listForTeachingAndMentorship,
    listForCommunity,
    listForAngelInvestment,
    listForMentionInMedia,
    listForEntrepreneurship,
    listForCompany,
    checkIsValid,
    isValid,
    setIsValid,
    onValidityChange,
  ]);
};

export const getIsRefereesDetailsValid = (
  application: ApplicationFull,
  listForReferees: ApplicantReferee[]
): boolean => {
  const isValid = listForReferees.every(
    (x) =>
      getApplicationSectionStatusFromArrayItem(
        application,
        ApplicationSectionColumn.RefereeDetails,
        x.id
      ) === ApplicationSectionStatusType.ReviewCompleted
  );
  return isValid;
};

const isExternalProfileSubmitPassed = (application: ApplicationFull) => {
  const section = getApplicationSection(application);

  if (
    section.externalProfileStatusId &&
    [
      ApplicationSectionStatusType.SubmittedForReview,
      ApplicationSectionStatusType.ReviewCompleted,
    ].includes(section.externalProfileStatusId)
  ) {
    return true;
  }

  if (section.externalProfileReviewIteration) {
    return true;
  }

  return false;
};

const isExternalProfileFinished = (application: ApplicationFull) => {
  const section = getApplicationSection(application);

  if (
    section.externalProfileStatusId ===
    ApplicationSectionStatusType.ReviewCompleted
  ) {
    return true;
  }

  return false;
};

const isProfJourneyOutlineSubmitPassed = (application: ApplicationFull) => {
  const section = getApplicationSection(application);

  if (
    section.profJourneyOutlineStatusId &&
    [
      ApplicationSectionStatusType.SubmittedForReview,
      ApplicationSectionStatusType.ReviewCompleted,
    ].includes(section.profJourneyOutlineStatusId)
  ) {
    return true;
  }

  if (section.profJourneyOutlineReviewIteration) {
    return true;
  }

  return false;
};

const isProfJourneyOutlineFinished = (application: ApplicationFull) => {
  const section = getApplicationSection(application);

  if (
    section.profJourneyOutlineStatusId ===
    ApplicationSectionStatusType.ReviewCompleted
  ) {
    return true;
  }

  return false;
};

const isStepDocFinished = (application: ApplicationFull) => {
  const section = getApplicationSection(application);

  if (
    section.stepDocStatusId === ApplicationSectionStatusType.ReviewCompleted
  ) {
    return true;
  }

  return false;
};

const isPotentialRefereesSubmitPassed = (application: ApplicationFull) => {
  const section = getApplicationSection(application);

  if (
    section.potentialRefereesStatusId &&
    [
      ApplicationSectionStatusType.SubmittedForReview,
      ApplicationSectionStatusType.ReviewCompleted,
    ].includes(section.potentialRefereesStatusId)
  ) {
    return true;
  }

  if (section.potentialRefereesReviewIteration) {
    return true;
  }

  return false;
};

const isPotentialRefereesFinished = (application: ApplicationFull) => {
  const section = getApplicationSection(application);

  if (
    section.potentialRefereesStatusId ===
    ApplicationSectionStatusType.ReviewCompleted
  ) {
    return true;
  }

  return false;
};

export const getApplicationProgress = (application?: ApplicationFull) => {
  if (!application) {
    return null;
  }

  const section = getApplicationSection(application);

  let resultWeight = 2;

  if (isExternalProfileSubmitPassed(application)) {
    resultWeight += 15;
  }

  if (isExternalProfileFinished(application)) {
    resultWeight += 15;
  }

  if (application.isLegacyProfJourney) {
    let profJourneyWeight = 0;
    [
      ...(section.companyIdStatusId || []),
      ...(section.entrepreneurshipIdStatusId || []),
    ].forEach((experience: any) => {
      if (
        experience?.statusId === ApplicationSectionStatusType.ReviewCompleted
      ) {
        profJourneyWeight += 10;
      }
    });
    resultWeight += Math.min(profJourneyWeight, 20);
  } else {
    if (isProfJourneyOutlineSubmitPassed(application)) {
      resultWeight += 5;
    }

    if (isProfJourneyOutlineFinished(application)) {
      resultWeight += 5;
    }

    const projectDetailsRecords = application.applicationForm?.reduce<
      ApplicationForm[]
    >((acc, formRecord) => {
      if (
        [
          ProfJourneyFormNamespace.CompanyEmployment,
          ProfJourneyFormNamespace.CompanyEntrepreneurship,
        ].includes(formRecord.namespace as ProfJourneyFormNamespace)
      ) {
        const projectFormRecords = formRecord?.children?.filter(
          (companyChildFormRecord) =>
            [
              ProfJourneyFormNamespace.ProjectEmployment,
              ProfJourneyFormNamespace.ProjectEntrepreneurship,
            ].includes(
              companyChildFormRecord.namespace as ProfJourneyFormNamespace
            )
        );

        projectFormRecords?.forEach((projectFormRecord) => {
          const projectDetailsForm = projectFormRecord.children?.find(
            (companyChildFormRecord) =>
              [
                ProfJourneyFormNamespace.ProjectDetailsEmployment,
                ProfJourneyFormNamespace.ProjectDetailsEntrepreneurship,
              ].includes(
                companyChildFormRecord.namespace as ProfJourneyFormNamespace
              )
          );

          if (projectDetailsForm) {
            acc.push(projectDetailsForm);
          }
        });
      }

      return acc;
    }, []);

    let profJourneySubmitedProjectsWeight = 0;
    projectDetailsRecords?.forEach((projectDetailsRecord) => {
      if (
        projectDetailsRecord.statusId &&
        [
          ApplicationSectionStatusType.SubmittedForReview,
          ApplicationSectionStatusType.ReviewCompleted,
        ].includes(projectDetailsRecord.statusId)
      ) {
        profJourneySubmitedProjectsWeight += 2;
      }

      if (projectDetailsRecord.reviewIteration) {
        profJourneySubmitedProjectsWeight += 2;
      }
    });
    resultWeight += Math.min(profJourneySubmitedProjectsWeight);

    let profJourneyFinishedProjectsWeight = 0;
    projectDetailsRecords?.forEach((projectDetailsRecord) => {
      if (
        projectDetailsRecord.statusId ===
        ApplicationSectionStatusType.ReviewCompleted
      ) {
        profJourneyFinishedProjectsWeight += 2;
      }
    });
    resultWeight += Math.min(profJourneyFinishedProjectsWeight, 10);
  }

  if (isStepDocFinished(application)) {
    resultWeight += 10;
  }

  if (isPotentialRefereesSubmitPassed(application)) {
    resultWeight += 10;
  }

  if (isPotentialRefereesFinished(application)) {
    resultWeight += 10;
  }

  let refereeDetailsWeight = 0;
  section.refereeIdStatusId?.forEach((referee: any) => {
    if (referee?.statusId === ApplicationSectionStatusType.ReviewCompleted) {
      refereeDetailsWeight += 3;
    }
  });
  resultWeight += Math.min(refereeDetailsWeight, 10);

  return Math.min(resultWeight, 100);
};

export function getApplicationSection(application: ApplicationFull) {
  /**
   * Some legacy / partially-initialised applications may not yet have a related
   * `applicationSection` row. Previously this function returned `undefined`
   * which caused runtime errors when callers immediately accessed properties,
   * e.g. `getApplicationSection(application).isProvideDocsUnlocked`.
   *
   * To make this helper safe we always return an object – if there is no
   * `applicationSection` record we fall back to an empty object. Call sites
   * are already written to handle `undefined` properties (via `||` fallbacks),
   * so this change prevents 500 errors for such applications without changing
   * existing behaviour.
   */
  return (application.applicationSection?.[0] ??
    {}) as ApplicationFull["applicationSection"][0];
}
